import { Typography } from "antd";
import React, { ReactNode, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import AppContent from "../../components/AppContent";
import Downloading from "../../components/import-export/Downloading";
import Initial from "../../components/import-export/Initial";
import Saved from "../../components/import-export/Saved";
import Saving from "../../components/import-export/Saving";
import Uploading from "../../components/import-export/Uploading";
import ValidationFailure from "../../components/import-export/ValidationFailure";
import ValidationSuccess from "../../components/import-export/ValidationSuccess";
import { RightIndentWrapper } from "../../spaces";
import State from "../../State";
import intl from "../../utils/intl";
import * as actions from "./actions";
import { Agreement } from "./Agreement";
import AgreementTable from "./components/AgreementTable";
import AgreementTableProps from "./components/AgreementTable/props";
import AgreementValidationTable from "./components/AgreementValidationTable";
import CreateAgreementDialog from "./components/CreateAgreementDialog";
import CreateButton from "./components/CreateButton";
import messages from "./messages";
import AgreementsPageState from "./state";

const AgreementsWrapper = styled.div`
  padding-top: 20px;
`;

const AgreementsLayout = styled(AppContent)`
  padding: 30px;
`;

function getImportExportContent(
  state: AgreementsPageState,
  uploadAgreements: (file: File) => void,
  completeAgreementsUpload: () => void,
  saveAgreements: (agreementImportId: string) => void,
  downloadAgreements: () => void
): ReactNode {
  switch (state.importExportAgreementsState.type) {
    case "initial":
      return (
        <Initial
          upload={uploadAgreements}
          download={downloadAgreements}
          chooseOperationMessage={intl.formatMessage(messages.chooseOperation)}
        />
      );
    case "saved":
      return (
        <Saved
          messages={{
            saved: messages.agreementsSaved,
            return: messages.agreementsImportComplete,
          }}
          completeUpload={completeAgreementsUpload}
          totalCount={state.importExportAgreementsState.totalCount}
        />
      );
    case "saving":
      return (
        <Saving
          messages={{
            saving: messages.savingAgreements,
          }}
        />
      );
    case "uploading":
      return (
        <Uploading
          messages={{
            uploading: messages.uploadingAgreements,
          }}
        />
      );
    case "upload-succeeded":
      return (
        <ValidationSuccess
          messages={{
            toBeSaved: messages.agreementsToBeSaved,
            returnTo: messages.cancelAgreementsImport,
            save: messages.save,
          }}
          validatedData={
            <>
              <Typography.Title level={5}>
                {intl.formatMessage(messages.agreementsValidCount, {
                  totalCount:
                    state.importExportAgreementsState.agreements.length,
                })}
              </Typography.Title>
              <AgreementValidationTable
                agreements={state.importExportAgreementsState.agreements}
              />
            </>
          }
          importId={state.importExportAgreementsState.agreementImportId}
          return={completeAgreementsUpload}
          save={saveAgreements}
        />
      );
    case "upload-failed":
      return (
        <ValidationFailure
          messages={{
            uploadFailed: messages.agreementsUploadFailed,
            errors: messages.errors,
            returnTo: messages.cancelAgreementsImport,
          }}
          errors={state.importExportAgreementsState.errors}
          complete={completeAgreementsUpload}
        />
      );
    case "downloading":
      return (
        <Downloading
          messages={{ chooseOperation: messages.chooseOperation }}
          downloading={true}
        />
      );
  }
}

const AgreementsPage: React.FC = () => {
  const dispatch = useDispatch();

  const uploadAgreements = (file: File): void => {
    dispatch(actions.uploadAgreements(file as File));
  };

  const completeAgreementsUpload = (): void => {
    dispatch(actions.completeAgreementsUpload());
  };

  const saveAgreements = (agreementImportId: string): void => {
    dispatch(actions.saveAgreements(agreementImportId));
  };

  const downloadAgreements = (): void => {
    dispatch(actions.downloadAgreements());
  };

  const showDialog = () => {
    dispatch(actions.showCreateAgreementDialog());
  };

  const hideDialog = () => {
    dispatch(actions.hideCreateAgreementDialog());
  };

  const createAgreement = (agreement: Agreement) => {
    dispatch(actions.createAgreement(agreement));
  };

  const state = useSelector<State, AgreementsPageState>(
    (state) => state.agreements
  );

  useEffect(() => {
    if (state.loaded || state.loading) {
      return;
    }

    dispatch(actions.loadAgreements());
  }, [state.loaded, state.loading, dispatch]);

  const tableProps: AgreementTableProps = {
    loading: state.loading,
    agreements: state.agreements,
    delete: (agreement: Agreement): void => {
      dispatch(actions.deleteAgreement(agreement));
    },
    deletingAgreement: state.deleting.agreement,
  };

  return (
    <>
      <AgreementsLayout>
        {getImportExportContent(
          state,
          uploadAgreements,
          completeAgreementsUpload,
          saveAgreements,
          downloadAgreements
        )}
      </AgreementsLayout>

      <AgreementsWrapper>
        <AgreementsLayout>
          <RightIndentWrapper>
            <CreateButton onClick={showDialog} />
          </RightIndentWrapper>
          <AgreementTable {...tableProps} />
        </AgreementsLayout>
      </AgreementsWrapper>

      <CreateAgreementDialog
        visible={state.createDialog.visible}
        creating={state.createDialog.creating}
        create={createAgreement}
        cancel={hideDialog}
      />
    </>
  );
};

export default AgreementsPage;
